import { useEffect, useState, useCallback } from "react";
import { Button, Input, Select, EmptyState } from "@/admin/ui";
import { toast } from "sonner";
import { ActionIcon } from "@/user/workspace/wsui";
import { fmtDate } from "@/user/workspace/ws.api";
import { ceoApi } from "@/user/ceo/ceoApi";
import { ACTION_LABEL, PRIMARY_ACTIONS, DANGER_ACTIONS, STATE_STYLE, PRIORITY_STYLE, prettyState, CARD_ICON } from "@/user/ceo/cards.config";

export default function DecisionCards() {
    const [cards, setCards] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [filters, setFilters] = useState({ q: "", priority: "", state: "", card_type: "" });
    const [active, setActive] = useState(null);
    const [library, setLibrary] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
            const [c, s] = await Promise.all([ceoApi.cards(params), ceoApi.cardsSummary()]);
            setCards(c);
            setSummary(s);
        } catch {
            toast.error("Could not load decision cards");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { ceoApi.cardsLibrary().then(setLibrary).catch(() => {}); }, []);

    async function generate() {
        setBusy(true);
        try {
            const r = await ceoApi.generateCards();
            toast.success(r.created ? `${r.created} new card${r.created > 1 ? "s" : ""} generated` : "All caught up — no new cards");
            load();
        } catch {
            toast.error("Generation failed");
        } finally {
            setBusy(false);
        }
    }

    async function doAction(card, action) {
        try {
            const updated = await ceoApi.cardAction(card.id, action);
            toast.success(`${ACTION_LABEL[action] || action} recorded`);
            setActive(updated);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    }

    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    return (
        <div data-testid="ceo-decision-cards">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-display text-2xl font-medium text-black">Decision Cards</h2>
                    <p className="text-[13px] text-neutral-500 mt-1">Interactive business cards derived from live department data. AI reasoning arrives next phase.</p>
                </div>
                <Button variant="primary" onClick={generate} disabled={busy} data-testid="cards-generate">
                    {busy ? "Generating…" : "Generate from data"}
                </Button>
            </div>

            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 mb-6">
                    {[["Total", summary.total], ...Object.entries(summary.by_state).map(([k, v]) => [prettyState(k), v])].slice(0, 7).map(([label, val], i) => (
                        <div key={i} className="glass rounded-xl p-3" data-testid={`cards-stat-${i}`}>
                            <div className="text-[9.5px] font-semibold tracking-[0.14em] uppercase text-neutral-500 truncate">{label}</div>
                            <div className="mt-1 font-display text-xl font-medium text-black">{val}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="glass rounded-2xl p-3.5 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Input placeholder="Search cards" value={filters.q} onChange={(e) => set("q", e.target.value)} data-testid="cards-search" />
                <Select value={filters.priority} onChange={(e) => set("priority", e.target.value)} data-testid="cards-filter-priority">
                    <option value="">All priorities</option>
                    {["low", "medium", "high", "critical"].map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
                <Select value={filters.state} onChange={(e) => set("state", e.target.value)} data-testid="cards-filter-state">
                    <option value="">All states</option>
                    {["generated", "viewed", "pending_action", "approved", "rejected", "executed", "archived"].map((s) => <option key={s} value={s}>{prettyState(s)}</option>)}
                </Select>
                <Select value={filters.card_type} onChange={(e) => set("card_type", e.target.value)} data-testid="cards-filter-type">
                    <option value="">All types</option>
                    {library.map((l) => <option key={l.type} value={l.type}>{l.label}</option>)}
                </Select>
            </div>

            {loading ? (
                <div className="text-neutral-500 py-16 text-center">Loading…</div>
            ) : cards.length === 0 ? (
                <EmptyState title="No decision cards yet" hint="Click ‘Generate from data’ to turn your team’s submissions into actionable cards." />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cards-grid">
                    {cards.map((c) => <SnippetCard key={c.id} card={c} onOpen={() => { setActive(c); doAction(c, "view_details"); }} onAction={doAction} />)}
                </div>
            )}

            {active && <CardDrawer card={active} onClose={() => setActive(null)} onAction={doAction} />}
        </div>
    );
}

function SnippetCard({ card, onOpen, onAction }) {
    const quick = card.actions.filter((a) => a !== "view_details").slice(0, 2);
    return (
        <div data-testid={`snippet-card-${card.id}`} className="relative glass rounded-2xl p-5 flex flex-col overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_18px_50px_-24px_rgba(139,92,246,0.35)] transition-all duration-300">
            <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(120% 90% at 100% 0%, rgba(139,92,246,0.07), transparent 55%)" }} />
            <div className="relative">
                <div className="flex items-start justify-between gap-2">
                    <span className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center shrink-0">
                        <ActionIcon name={CARD_ICON[card.card_type] || "flag"} />
                    </span>
                    <div className="flex items-center gap-1.5">
                        <Pill cls={PRIORITY_STYLE[card.priority] || PRIORITY_STYLE.medium}>{card.priority}</Pill>
                        <Pill cls={STATE_STYLE[card.state] || STATE_STYLE.generated}>{prettyState(card.state)}</Pill>
                    </div>
                </div>
                <div className="mt-3 text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-600">{card.card_label}</div>
                <button onClick={onOpen} className="text-left mt-1 font-display text-[17px] font-medium text-black leading-snug hover:text-violet-700 transition-colors" data-testid={`snippet-open-${card.id}`}>
                    {card.title}
                </button>
                <div className="mt-1 text-[11px] text-neutral-400">{card.department} · {fmtDate(card.created_at)}</div>
                <p className="mt-2.5 text-[13px] text-neutral-600 leading-relaxed line-clamp-2">{card.summary}</p>

                {card.metrics?.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        {card.metrics.slice(0, 2).map((m, i) => (
                            <div key={i} className="rounded-xl bg-neutral-50/70 border border-black/5 p-2">
                                <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-neutral-400 truncate">{m.label}</div>
                                <div className="text-[13px] font-medium text-black truncate">{m.value}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {quick.map((a) => (
                        <button key={a} onClick={() => onAction(card, a)} data-testid={`snippet-action-${a}-${card.id}`}
                            className={`text-[12px] font-medium px-3 py-1.5 rounded-full transition-colors ${PRIMARY_ACTIONS.has(a) ? "bg-black text-white hover:bg-neutral-800" : DANGER_ACTIONS.has(a) ? "bg-white text-rose-600 border border-rose-200 hover:bg-rose-50" : "bg-white text-black border border-black/10 hover:bg-neutral-50"}`}>
                            {ACTION_LABEL[a] || a}
                        </button>
                    ))}
                    <button onClick={onOpen} className="text-[12px] text-violet-600 hover:text-violet-700 ml-auto">Details →</button>
                </div>
            </div>
        </div>
    );
}

function CardDrawer({ card, onClose, onAction }) {
    return (
        <div className="fixed inset-0 z-[120] flex justify-end" data-testid="card-drawer">
            <div className="absolute inset-0 bg-white/60 backdrop-blur-xl" onClick={onClose} />
            <div className="relative w-full max-w-lg h-full bg-white/90 backdrop-blur-2xl border-l border-black/10 overflow-y-auto p-6 sm:p-8 anim-fade-up">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-violet-600">{card.card_label}</div>
                        <h3 className="mt-1 font-display text-2xl font-medium text-black">{card.title}</h3>
                    </div>
                    <button onClick={onClose} data-testid="card-drawer-close" className="h-9 w-9 rounded-full hover:bg-black/5 inline-flex items-center justify-center">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" /></svg>
                    </button>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Pill cls={PRIORITY_STYLE[card.priority] || PRIORITY_STYLE.medium}>{card.priority}</Pill>
                    <Pill cls={STATE_STYLE[card.state] || STATE_STYLE.generated}>{prettyState(card.state)}</Pill>
                    <span className="text-[11px] text-neutral-400">{card.department} · {fmtDate(card.created_at)}</span>
                </div>

                <div className="mt-5">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400">Executive Summary</div>
                    <p className="mt-1 text-[14px] text-neutral-700 leading-relaxed">{card.summary}</p>
                </div>

                {card.metrics?.length > 0 && (
                    <div className="mt-5">
                        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-2">Supporting Metrics</div>
                        <div className="grid grid-cols-2 gap-2.5">
                            {card.metrics.map((m, i) => (
                                <div key={i} className="rounded-xl bg-neutral-50 border border-black/5 p-3">
                                    <div className="text-[9.5px] font-semibold tracking-[0.12em] uppercase text-neutral-400">{m.label}</div>
                                    <div className="text-[15px] font-medium text-black">{m.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mt-5 rounded-xl border border-violet-100 bg-violet-50/50 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-violet-600">Recommended Action</div>
                        <span className="text-[10px] font-semibold text-neutral-400">Confidence · Waiting for AI</span>
                    </div>
                    <p className="mt-1 text-[13px] text-neutral-700">{card.recommended_action}</p>
                </div>

                <div className="mt-6">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-2">Actions</div>
                    <div className="flex flex-wrap gap-2">
                        {card.actions.map((a) => (
                            <button key={a} onClick={() => onAction(card, a)} data-testid={`drawer-action-${a}`}
                                className={`text-[12.5px] font-medium px-3.5 py-2 rounded-full transition-colors ${PRIMARY_ACTIONS.has(a) ? "bg-black text-white hover:bg-neutral-800" : DANGER_ACTIONS.has(a) ? "bg-white text-rose-600 border border-rose-200 hover:bg-rose-50" : "bg-white text-black border border-black/10 hover:bg-neutral-50"}`}>
                                {ACTION_LABEL[a] || a}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-6">
                    <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-2">Audit History</div>
                    <ul className="space-y-2">
                        {(card.audit || []).slice().reverse().map((a, i) => (
                            <li key={i} className="flex items-center justify-between text-[12px] text-neutral-600 border-b border-black/5 pb-2">
                                <span><span className="font-medium text-black">{prettyState(a.action)}</span> · {a.by}</span>
                                <span className="text-neutral-400">{fmtDate(a.at)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

function Pill({ cls, children }) {
    return <span className={`inline-flex items-center text-[10px] font-semibold tracking-[0.1em] uppercase rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}
